import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import hrmsNotificationService from "../services/hrmsNotificationService";
import { useAuth } from "./AuthContext";
import { useTelemetry } from "../hooks/useTelemetry";

export interface HRMSNotification {
  id: string;
  title: string;
  message: string;
  type: "success" | "info" | "warning" | "error";
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionText?: string;
  category?: "system" | "user" | "security" | "update" | "approval" | "review";
  priority?: "low" | "medium" | "high" | "critical";
  entityType?: string;
  entityId?: string;
  actionData?: Record<string, any>;
  sender?: {
    id: string;
    name: string;
    email: string;
  };
}

interface HRMSNotificationContextType {
  notifications: HRMSNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAsUnread: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refetchNotifications: () => Promise<void>;
}

const HRMSNotificationContext = createContext<
  HRMSNotificationContextType | undefined
>(undefined);

export const HRMSNotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const { logError: logTelemetryError } = useTelemetry();
  const [notifications, setNotifications] = useState<HRMSNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await hrmsNotificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (err) {
      logTelemetryError(
        {
          name: err instanceof Error ? err.name : 'NotificationError',
          message: err instanceof Error ? err.message : 'Failed to mark notification as read',
          stack: err instanceof Error ? err.stack : undefined,
          component: 'HRMSNotificationContext',
          route: window.location.pathname,
          action: 'markAsRead',
          severity: 'medium',
        },
        { notificationId }
      );
      setError("Failed to mark notification as read");
    }
  }, [logTelemetryError]);

  const markAsUnread = useCallback(async (notificationId: string) => {
    try {
      await hrmsNotificationService.markAsUnread(notificationId);
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: false }
            : notification
        )
      );
    } catch (err) {
      logTelemetryError(
        {
          name: err instanceof Error ? err.name : 'NotificationError',
          message: err instanceof Error ? err.message : 'Failed to mark notification as unread',
          stack: err instanceof Error ? err.stack : undefined,
          component: 'HRMSNotificationContext',
          route: window.location.pathname,
          action: 'markAsUnread',
          severity: 'medium',
        },
        { notificationId }
      );
      setError("Failed to mark notification as unread");
    }
  }, [logTelemetryError]);

  const markAllAsRead = useCallback(async () => {
    try {
      const result = await hrmsNotificationService.markAllAsRead();
      if (result.success) {
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, read: true }))
        );
      }
    } catch (err) {
      logTelemetryError(
        {
          name: err instanceof Error ? err.name : 'NotificationError',
          message: err instanceof Error ? err.message : 'Failed to mark all notifications as read',
          stack: err instanceof Error ? err.stack : undefined,
          component: 'HRMSNotificationContext',
          route: window.location.pathname,
          action: 'markAllAsRead',
          severity: 'medium',
        }
      );
      setError("Failed to mark all notifications as read");
    }
  }, [logTelemetryError]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await hrmsNotificationService.deleteNotification(notificationId);
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== notificationId)
      );
    } catch (err) {
      logTelemetryError(
        {
          name: err instanceof Error ? err.name : 'NotificationError',
          message: err instanceof Error ? err.message : 'Failed to delete notification',
          stack: err instanceof Error ? err.stack : undefined,
          component: 'HRMSNotificationContext',
          route: window.location.pathname,
          action: 'deleteNotification',
          severity: 'medium',
        },
        { notificationId }
      );
      setError("Failed to delete notification");
    }
  }, [logTelemetryError]);

  const clearAllNotifications = useCallback(async () => {
    try {
      const result = await hrmsNotificationService.clearAllNotifications();
      if (result.success) {
        setNotifications([]);
      }
    } catch (err) {
      logTelemetryError(
        {
          name: err instanceof Error ? err.name : 'NotificationError',
          message: err instanceof Error ? err.message : 'Failed to clear notifications',
          stack: err instanceof Error ? err.stack : undefined,
          component: 'HRMSNotificationContext',
          route: window.location.pathname,
          action: 'clearAllNotifications',
          severity: 'medium',
        }
      );
      setError("Failed to clear notifications");
    }
  }, [logTelemetryError]);

  const refetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await hrmsNotificationService.getNotifications();

      if (response.success) {
        const transformedNotifications: HRMSNotification[] =
          response.data.map((item: any) => ({
            id: item.id,
            title: item.title,
            message: item.message,
            type: item.type,
            timestamp: new Date(item.timestamp),
            read: item.read,
            actionUrl: item.actionUrl,
            actionText: item.actionText,
            category: item.category,
            priority: item.priority,
            entityType: item.entityType,
            entityId: item.entityId,
            actionData: item.actionData,
            sender: item.sender,
          }));
        setNotifications(transformedNotifications);
      }
    } catch (err) {
      logTelemetryError(
        {
          name: err instanceof Error ? err.name : 'NotificationError',
          message: err instanceof Error ? err.message : 'Failed to fetch HRMS notifications',
          stack: err instanceof Error ? err.stack : undefined,
          component: 'HRMSNotificationContext',
          route: window.location.pathname,
          action: 'refetchNotifications',
          severity: 'high',
        }
      );
      setError("Failed to fetch HRMS notifications");
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, logTelemetryError]);

  useEffect(() => {
    if (isAuthenticated) {
      refetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [isAuthenticated, refetchNotifications]);

  const contextValue = useMemo<HRMSNotificationContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    ]
  );

  return (
    <HRMSNotificationContext.Provider value={contextValue}>
      {children}
    </HRMSNotificationContext.Provider>
  );
};

export const useHRMSNotifications = (): HRMSNotificationContextType => {
  const context = useContext(HRMSNotificationContext);

  if (context === undefined) {
    throw new Error(
      "useHRMSNotifications must be used within a HRMSNotificationProvider"
    );
  }

  return context;
};
